import React, { useState } from 'react';
import { SPECIAL_CHARS_DISPLAY } from '../utils/passwordPolicy';

// Full-screen (well, full-card) walkthrough shown before the actual
// registration form — see Register.jsx, which renders this in place of
// its normal content while `showIntro` is true, inside the SAME page/card
// chrome (background image, glass card, Home button) so it reads as one
// continuous surface rather than a separate modal.
//
// Pure CSS keyframe animations, no new dependency — the codebase has no
// animation library installed and this is simple enough not to need one.

const GOLD = '#D4A94C';
const BLUE = '#009FE3';
const TEXT_MUTED = 'rgba(255, 255, 255, 0.75)';

const KEYFRAMES = `
@keyframes ri-slidein { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes ri-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
@keyframes ri-draw { to { stroke-dashoffset: 0; } }
@keyframes ri-pencil { 0% { transform: translate(0, 0) rotate(-6deg); } 50% { transform: translate(6px, -14px) rotate(-6deg); } 100% { transform: translate(0, 0) rotate(-6deg); } }
@keyframes ri-pop { 0% { transform: scale(0); opacity: 0; } 70% { transform: scale(1.15); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
@keyframes ri-scan { 0%, 100% { transform: translateY(-14px); opacity: 0.3; } 50% { transform: translateY(14px); opacity: 0.9; } }
`;

function IconShell({ children }) {
  return (
    <div style={{ width: '96px', height: '96px', margin: '0 auto 20px', animation: 'ri-float 3.5s ease-in-out infinite' }}>
      {children}
    </div>
  );
}

function FormIcon() {
  return (
    <IconShell>
      <svg viewBox="0 0 96 96" fill="none">
        <rect x="18" y="12" width="52" height="68" rx="6" stroke={BLUE} strokeWidth="3" fill="rgba(0,159,227,0.06)" />
        {[28, 42, 56].map((y, i) => (
          <line
            key={y}
            x1="28" y1={y} x2="60" y2={y}
            stroke={GOLD} strokeWidth="3" strokeLinecap="round"
            strokeDasharray="32" strokeDashoffset="32"
            style={{ animation: `ri-draw 0.6s ease forwards ${0.3 + i * 0.35}s` }}
          />
        ))}
        <g style={{ animation: 'ri-pencil 1.8s ease-in-out infinite' }}>
          <path d="M62 58 L78 42 L84 48 L68 64 L60 66 Z" fill="rgba(212,169,76,0.25)" stroke={GOLD} strokeWidth="3" strokeLinejoin="round" />
        </g>
      </svg>
    </IconShell>
  );
}

function VerifyIcon() {
  return (
    <IconShell>
      <svg viewBox="0 0 96 96" fill="none">
        <rect x="10" y="24" width="34" height="48" rx="6" stroke={BLUE} strokeWidth="3" />
        <line x1="20" y1="66" x2="34" y2="66" stroke={BLUE} strokeWidth="3" strokeLinecap="round" />
        <rect x="50" y="30" width="36" height="26" rx="4" stroke={BLUE} strokeWidth="3" />
        <path d="M50 32 L68 46 L86 32" stroke={BLUE} strokeWidth="3" strokeLinejoin="round" fill="none" />
        <g style={{ animation: 'ri-pop 0.6s ease 0.5s both' }}>
          <circle cx="68" cy="70" r="16" fill="rgba(212,169,76,0.2)" stroke={GOLD} strokeWidth="3" />
          <path d="M61 70 L66 75 L76 63" stroke={GOLD} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </g>
      </svg>
    </IconShell>
  );
}

function TotpIcon() {
  return (
    <IconShell>
      <svg viewBox="0 0 96 96" fill="none">
        <rect x="16" y="16" width="56" height="56" rx="6" stroke={BLUE} strokeWidth="3" fill="rgba(0,159,227,0.06)" />
        {[[24, 24], [24, 40], [24, 56], [40, 24], [56, 24], [56, 40], [40, 56], [56, 56]].map(([x, y]) => (
          <rect key={`${x}-${y}`} x={x} y={y} width="8" height="8" fill={BLUE} opacity="0.55" />
        ))}
        <line x1="16" y1="44" x2="72" y2="44" stroke={GOLD} strokeWidth="2.5" opacity="0.8" style={{ animation: 'ri-scan 2.2s ease-in-out infinite' }} />
        <g style={{ animation: 'ri-pop 0.6s ease 0.6s both' }}>
          <circle cx="76" cy="76" r="16" fill="rgba(212,169,76,0.2)" stroke={GOLD} strokeWidth="3" />
          <path d="M69 76 L74 81 L84 69" stroke={GOLD} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </g>
      </svg>
    </IconShell>
  );
}

const SLIDES = [
  {
    key: 'form',
    title: 'Fill your details in Registration form',
    Icon: FormIcon,
    fields: [
      { label: 'Username', detail: 'Must not contain spaces.' },
      { label: 'Email address', detail: 'Officers must use a BESCOM domain email id (@bescom.co.in). Non-BESCOM domain email id accounts require admin approval after registration.' },
      { label: 'Phone number', detail: 'Enter your official phone number — if you don’t have one, use your personal phone number.' },
      { label: 'Password', detail: `8+ characters with letters, numbers, and a special character (${SPECIAL_CHARS_DISPLAY}). Avoid common passwords like Bescom@123, names, or phone numbers.` },
    ],
  },
  {
    key: 'verify',
    title: 'Verify your phone & email',
    body: "We'll send a one-time code to your phone and a confirmation link to your email, just to make sure it's really you.",
    Icon: VerifyIcon,
  },
  {
    key: 'mfa-choice',
    title: "Choose 2nd factor method you will use to login",
    body: (
      <>
        Pick whichever suits you: scan a QR code with your <strong><em>authenticator app</em></strong>, use your{' '}
        <strong><em>fingerprint/PIN/face as a passkey</em></strong>, or get a{' '}
        <strong><em>one-time code by phone or email</em></strong>. That's it — you're ready to go!
      </>
    ),
    Icon: TotpIcon,
    guides: [
      { label: 'Google Authenticator guide', src: '/google-authenticator-guide.png' },
      { label: 'Microsoft Authenticator guide', src: '/microsoft-authenticator-guide.png' },
    ],
  },
];

const titleStyle = {
  fontSize: '19px',
  fontWeight: '700',
  color: '#fff',
  margin: '0 0 10px',
  textAlign: 'center',
};

const bodyStyle = {
  fontSize: '14px',
  lineHeight: '1.55',
  color: TEXT_MUTED,
  margin: '0 0 18px',
  textAlign: 'center',
  minHeight: '66px',
};

const fieldsWrap = {
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  gap: '11px',
  margin: '0 0 18px',
};

const fieldLabel = {
  color: GOLD,
  fontWeight: '700',
  fontSize: '13px',
};

const fieldDetail = {
  color: TEXT_MUTED,
  fontSize: '13px',
  lineHeight: '1.5',
};

const guideCard = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  textDecoration: 'none',
  width: '175px',
};

const guideThumb = {
  width: '100%',
  height: '115px',
  objectFit: 'cover',
  objectPosition: 'top',
  borderRadius: '8px',
  border: `2px solid ${GOLD}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
};

const guideLabel = {
  fontSize: '14px',
  fontWeight: '600',
  color: GOLD,
  textAlign: 'center',
  lineHeight: '1.3',
};

const linkBadge = {
  display: 'inline-block',
  fontSize: '12px',
  fontWeight: '600',
  color: '#2c1f04',
  background: GOLD,
  border: `1px solid ${GOLD}`,
  borderRadius: '999px',
  padding: '6px 14px',
  textDecoration: 'none',
  boxShadow: '0 2px 8px rgba(212, 169, 76, 0.35)',
};

const dotsWrap = {
  display: 'flex',
  justifyContent: 'center',
  gap: '8px',
  margin: '18px 0',
};

function dotStyle(active) {
  return {
    width: active ? '22px' : '8px',
    height: '8px',
    borderRadius: '999px',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    background: active ? GOLD : 'rgba(255,255,255,0.3)',
    transition: 'all 0.25s ease',
  };
}

const navRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
};

const navBtnPrimary = {
  flex: 1,
  padding: '13px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '15px',
  fontWeight: '600',
  cursor: 'pointer',
};

const navBtnSecondary = {
  padding: '13px 18px',
  background: 'none',
  border: '1px solid rgba(255,255,255,0.35)',
  color: TEXT_MUTED,
  borderRadius: '10px',
  fontSize: '15px',
  cursor: 'pointer',
};

export default function RegistrationIntro({ onFinish }) {
  const [step, setStep] = useState(0);
  const slide = SLIDES[step];
  const isLast = step === SLIDES.length - 1;
  const { Icon } = slide;

  return (
    <div>
      <style>{KEYFRAMES}</style>

      <div key={slide.key} style={{ animation: 'ri-slidein 0.35s ease' }}>
        <Icon />
        <h2 style={titleStyle}>{slide.title}</h2>
        {slide.fields ? (
          <div style={fieldsWrap}>
            {slide.fields.map((f) => (
              <div key={f.label}>
                <span style={fieldLabel}>{f.label}: </span>
                <span style={fieldDetail}>{f.detail}</span>
              </div>
            ))}
          </div>
        ) : (
          <p style={bodyStyle}>{slide.body}</p>
        )}

        {slide.links && (
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '18px' }}>
            {slide.links.map((l) => (
              <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" style={linkBadge}>
                {l.label}
              </a>
            ))}
          </div>
        )}

        {slide.guides && (
          <p style={{ fontSize: '12px', color: TEXT_MUTED, textAlign: 'center', margin: '0 0 10px' }}>
            If you choose an authenticator app:
          </p>
        )}
        {slide.guides && (
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '18px' }}>
            {slide.guides.map((g) => (
              <a key={g.src} href={g.src} target="_blank" rel="noopener noreferrer" style={guideCard}>
                <img src={g.src} alt={g.label} style={guideThumb} />
                <span style={guideLabel}>{g.label} ↗</span>
              </a>
            ))}
          </div>
        )}
      </div>

      <div style={dotsWrap}>
        {SLIDES.map((s, i) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setStep(i)}
            style={dotStyle(i === step)}
            aria-label={`Go to step ${i + 1} of ${SLIDES.length}`}
          />
        ))}
      </div>

      <div style={navRow}>
        {step > 0 && (
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            style={navBtnSecondary}
          >
            Back
          </button>
        )}
        <button
          type="button"
          onClick={() => (isLast ? onFinish() : setStep((s) => s + 1))}
          style={navBtnPrimary}
        >
          {isLast ? 'Get started' : 'Next'}
        </button>
      </div>
    </div>
  );
}
